import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  Select,
  DatePicker,
  Button,
  message,
  Input,
  Row,
  Col,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { logApi, userApi, authApi } from '../../services/api';
import { UserLog, User } from '../../types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { requestDeduplication } from '../../utils/requestDeduplication';
import { usePermissions, usePermissionCheck } from '../../context/PermissionContext';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const LogManagement: React.FC = () => {
  const [logs, setLogs] = useState<UserLog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 筛选条件
  const [usernameFilter, setUsernameFilter] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string | undefined>();
  const [selectedIp, setSelectedIp] = useState<string>('');
  const [detailsFilter, setDetailsFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);

  // 权限检查
  const { loading: permissionLoading } = usePermissions();
  const { canViewUserLogs, canViewUsers } = usePermissionCheck();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!permissionLoading && canViewUserLogs()) {
      if (isAdmin || canViewUsers()) {
        fetchUsers();
      }
    }
  }, [permissionLoading, isAdmin, canViewUserLogs, canViewUsers]);

  useEffect(() => {
    if (!permissionLoading && canViewUserLogs()) {
      fetchLogs(current, pageSize, usernameFilter, selectedAction, selectedIp, detailsFilter, dateRange);
    }
  }, [permissionLoading, current, pageSize, usernameFilter, selectedAction, selectedIp, detailsFilter, dateRange]);

  useEffect(() => {
    authApi.getCurrentUser().then(res => {
      setIsAdmin(res.data.is_admin);
    });
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await requestDeduplication.request(
        'getUsersForSelection',
        () => userApi.getUsers({ page: 1, page_size: 100 })
      );
      setUsers(response.data.users);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  const fetchLogs = async (
    page = current,
    size = pageSize,
    username = usernameFilter,
    action = selectedAction,
    ip = selectedIp,
    details = detailsFilter,
    range = dateRange
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: size,
        username,
        action,
        ip: ip || undefined, // 空字符串转为undefined
        details: details || undefined, // 空字符串转为undefined
        start_time: range && range[0] ? range[0].startOf('day').toISOString() : undefined,
        end_time: range && range[1] ? range[1].endOf('day').toISOString() : undefined,
      };
      const response = await logApi.getAllLogs(params);
      const logsData = response.data.logs;
      setLogs(logsData);
      setTotal(response.data.total);
    } catch (error) {
      message.error('获取日志列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const handleRefresh = () => {
      fetchLogs(current, pageSize, usernameFilter, selectedAction, selectedIp, detailsFilter, dateRange);
    };
  };

  const handleClearFilters = () => {
    setUsernameFilter('');
    setSelectedAction(undefined);
    setSelectedIp('');
    setDetailsFilter('');
    const defaultRange: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().subtract(7, 'day'), dayjs()];
    setDateRange(defaultRange);
    setCurrent(1);
    // 清除筛选后重新拉取第一页数据
    fetchLogs(1, pageSize, '', undefined, '', '', defaultRange);
  };

  // 操作类型标签颜色映射
  const getActionTagColor = (action: string) => {
    const colorMap: Record<string, string> = {
      'login': 'green',
      'logout': 'orange',
      'create_user': 'blue',
      'update_user': 'cyan',
      'delete_user': 'red',
      'regen_api_key': 'purple',
      'add_balance': 'green',
      'deduct_balance': 'red',
      'api_call': 'default',
      'balance_change': 'gold',
      'user_update': 'cyan',
    };
    return colorMap[action] || 'default';
  };

  // 操作类型中文映射
  const getActionText = (action: string) => {
    const textMap: Record<string, string> = {
      'login': '登录',
      'logout': '登出',
      'create_user': '创建用户',
      'update_user': '更新用户',
      'delete_user': '删除用户',
      'regen_api_key': '重新生成API密钥',
      'add_balance': '充值',
      'deduct_balance': '扣费',
      'api_call': 'API调用',
      'balance_change': '余额变更',
      'user_update': '用户信息更新',
    };
    return textMap[action] || action;
  };

  const columns: ColumnsType<UserLog> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (userId: number) => {
        const user = users.find(u => u.id === userId);
        return (
          <div>
            <div>{userId}</div>
            {user && <div style={{ fontSize: '12px', color: '#666' }}>{user.name}</div>}
          </div>
        );
      },
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => (
        <Tag color={getActionTagColor(action)}>
          {getActionText(action)}
        </Tag>
      ),
    },
    {
      title: '操作详情',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '用户代理',
      dataIndex: 'user_agent',
      key: 'user_agent',
      ellipsis: true,
      width: 200,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      width: 180,
    },
  ];

  const canShowUserSelect = isAdmin || canViewUsers();

  // 权限检查失败时的展示
  if (!canViewUserLogs()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看用户日志的权限</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2}>日志管理</Title>
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            >
              清除筛选
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* 筛选条件 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {canShowUserSelect && (
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>用户搜索 (用户名)</div>
                  <Input
                    placeholder="输入用户名搜索"
                    value={usernameFilter}
                    onChange={e => setUsernameFilter(e.target.value)}
                    allowClear
                    prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                  />
                </div>
              </Col>
            )}

            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>操作类型</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择操作类型"
                  value={selectedAction}
                  onChange={setSelectedAction}
                  allowClear
                >
                  <Option value="login">登录</Option>
                  <Option value="logout">登出</Option>
                  <Option value="create_user">创建用户</Option>
                  <Option value="update_user">更新用户</Option>
                  <Option value="delete_user">删除用户</Option>
                  <Option value="regen_api_key">重新生成API密钥</Option>
                  <Option value="add_balance">充值</Option>
                  <Option value="deduct_balance">扣费</Option>
                  <Option value="api_call">API调用</Option>
                  <Option value="balance_change">余额变更</Option>
                  <Option value="user_update">用户信息更新</Option>
                </Select>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>IP地址</div>
                <Input
                  style={{ width: '100%' }}
                  placeholder="输入IP地址筛选"
                  value={selectedIp}
                  onChange={(e) => setSelectedIp(e.target.value)}
                  allowClear
                />
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>时间范围</div>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder={['开始时间', '结束时间']}
                />
              </div>
            </Col>

            <Col xs={24}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>操作详情搜索</div>
                <Input
                  placeholder="输入关键词搜索操作详情"
                  value={detailsFilter}
                  onChange={(e) => setDetailsFilter(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            current,
            pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size || 10);
              fetchLogs(page, size || 10, usernameFilter, selectedAction, selectedIp, detailsFilter, dateRange);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default LogManagement; 